import Box from '@mui/material/Box';
import Tooltip from '@mui/material/Tooltip';
import MUITypography from '@mui/material/Typography';
import { format } from 'date-fns';
import { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';

import ScheduleCardsLoader from './components/ScheduleCardsLoader';
import useScheduleCards from './hooks/useScheduleCards';
import {
  AmountStyled,
  CardWrapStyled,
  CardsRowStyled,
  ClaimReportInnerContentStyled,
  InnerContentRowStyled,
  RowLabelStyled,
  ScheduleTilesOuterWrapStyled,
  SuggestedAdjustmentsRowStyled,
} from './ScheduleCards.styled';
import FlexContainer from 'components/FlexContainer/FlexContainer';
import Typography from 'components/Typography/Typography';
import { useTickets } from 'contexts/TicketsContext';
import { ScheduleFilterValuesE, TicketsRefinementKeysE } from 'declarations/enums/tickets';
import { formatAmount } from 'helpers/locale';
import { useFetchTicketRequestCategories } from 'hooks/api/useTicketRequestCategories';
import AllScheduleIcon from 'icons/AllScheduleIcon';
import ArrowDownIcon from 'icons/ArrowDownIcon';

const enumMapping = [
  ScheduleFilterValuesE.LessThan31Days,
  ScheduleFilterValuesE.From31To60Days,
  ScheduleFilterValuesE.From61To90Days,
  ScheduleFilterValuesE.From91To120Days,
  ScheduleFilterValuesE.Over120Days,
];

type ScheduleCardsProps = {
  isSuggestedAdjustmentsOpened: boolean;
};

const ScheduleCards = ({ isSuggestedAdjustmentsOpened }: ScheduleCardsProps): JSX.Element => {
  const { scheduleReport, isLoadingScheduleReport } = useScheduleCards();
  const {
    updateSearchParamsArray,
    selectedRoofTop,
    activeScheduleCards,
    setActiveScheduleCards,
    activeScheduleFilters,
    setActiveScheduleFilters,
    ticketsCountByRoofTop,
    scheduleCategoryId,
    setScheduleCategoryId,
  } = useTickets();
  const [defaultRooftopId, setDefaultRooftopId] = useState<string>();

  const adjustmentSubCategoryIds = useMemo((): string[] => {
    const fromTotal =
      scheduleReport?.totalAdjustedDataBySubCategories?.map((s): string =>
        String(s.subCategoryId)
      ) ?? [];
    if (fromTotal.length > 0) return fromTotal;
    const firstWithData = scheduleReport?.data?.find(
      d => d.adjustedDataBySubCategories && d.adjustedDataBySubCategories.length > 0
    );
    const fromAdjusted =
      firstWithData?.adjustedDataBySubCategories?.map((s): string => String(s.subCategoryId)) ?? [];
    if (fromAdjusted.length > 0) return fromAdjusted;
    // Fallback: when API has no adjusted data, use dealer subcategories so isAdjustedView can be true when SubCategory is set
    return (
      scheduleReport?.totalDataBySubCategories?.map((s): string => String(s.subCategoryId)) ?? []
    );
  }, [scheduleReport]);

  useEffect(() => {
    // Set default rooftop ID based on ticketsCountByRoofTop
    if (ticketsCountByRoofTop && ticketsCountByRoofTop.countByRoofTop?.length > 0) {
      setDefaultRooftopId(ticketsCountByRoofTop.countByRoofTop[0].roofTopId);
    }
  }, [ticketsCountByRoofTop]);

  const categoriesResult = useFetchTicketRequestCategories(
    true,
    selectedRoofTop ?? defaultRooftopId
  );

  useEffect(() => {
    if (categoriesResult.data) {
      const scheduleCategory = categoriesResult.data.find(
        category => category.categoryName.toLowerCase() === 'schedule'
      );
      setScheduleCategoryId(scheduleCategory ? scheduleCategory.id : scheduleCategoryId);
    }
  }, [categoriesResult.data, scheduleCategoryId, setScheduleCategoryId]);

  const { search: locationSearch } = useLocation();
  const searchParams = useMemo(
    (): URLSearchParams => new URLSearchParams(locationSearch),
    [locationSearch]
  );

  const isAdjustedView = useMemo((): boolean => {
    const subParam = searchParams.get(TicketsRefinementKeysE.SubCategory);
    const subIds = subParam ? subParam.split(',').map((s): string => s.trim()) : [];
    return (
      adjustmentSubCategoryIds.length > 0 &&
      adjustmentSubCategoryIds.length === subIds.length &&
      adjustmentSubCategoryIds.every((id): boolean => subIds.includes(id))
    );
  }, [searchParams, adjustmentSubCategoryIds]);

  const activeAdjustedCards = useMemo((): string[] => {
    if (!isAdjustedView) return [];
    const scheduleVal = searchParams.get(TicketsRefinementKeysE.Schedule);
    const scheduleParts = scheduleVal ? scheduleVal.split(',') : [];
    const valueToCard: Record<string, string> = {
      [ScheduleFilterValuesE.LessThan31Days]: 'adjusted-1',
      [ScheduleFilterValuesE.From31To60Days]: 'adjusted-2',
      [ScheduleFilterValuesE.From61To90Days]: 'adjusted-3',
      [ScheduleFilterValuesE.From91To120Days]: 'adjusted-4',
      [ScheduleFilterValuesE.Over120Days]: 'adjusted-5',
    };
    if (scheduleParts.length === 0) {
      return ['adjusted-all', 'adjusted-1', 'adjusted-2', 'adjusted-3', 'adjusted-4', 'adjusted-5'];
    }
    const cards: string[] = scheduleParts.map((v): string => valueToCard[v] ?? '').filter(Boolean);
    if (scheduleParts.length === 5 && cards.length === 5) cards.push('adjusted-all');
    return cards;
  }, [searchParams, isAdjustedView]);

  const displayDealerCards = isAdjustedView ? [] : activeScheduleCards;

  const toggleAdjustedFilter = (cardKey: string, filterValue?: ScheduleFilterValuesE): void => {
    if (adjustmentSubCategoryIds.length === 0) return;
    setActiveScheduleCards([]);
    setActiveScheduleFilters([]);
    if (cardKey === 'adjusted-all') {
      const isActive = activeAdjustedCards.includes('adjusted-all');
      if (isActive) {
        updateSearchParamsArray(TicketsRefinementKeysE.SubCategory, null);
        updateSearchParamsArray(TicketsRefinementKeysE.Schedule, null);
      } else {
        updateSearchParamsArray(TicketsRefinementKeysE.SubCategory, [
          adjustmentSubCategoryIds.join(','),
        ]);
        updateSearchParamsArray(TicketsRefinementKeysE.Schedule, null);
      }
      return;
    }
    // Multi-select age buckets: add/remove this bucket like Dealer Schedule
    if (filterValue) {
      const scheduleVal = searchParams.get(TicketsRefinementKeysE.Schedule);
      const currentParts = scheduleVal ? scheduleVal.split(',') : [];
      const isCurrentlySelected = currentParts.includes(filterValue);
      let newFilters: ScheduleFilterValuesE[];
      if (isCurrentlySelected) {
        newFilters = currentParts.filter(
          (v): boolean => v !== filterValue
        ) as ScheduleFilterValuesE[];
      } else {
        newFilters = [...currentParts, filterValue] as ScheduleFilterValuesE[];
      }
      updateSearchParamsArray(TicketsRefinementKeysE.SubCategory, [
        adjustmentSubCategoryIds.join(','),
      ]);
      updateSearchParamsArray(
        TicketsRefinementKeysE.Schedule,
        newFilters.length === 0 || newFilters.length === 5 ? null : newFilters
      );
    }
  };

  useEffect(() => {
    // Derive adjusted view from current URL inside effect to avoid stale closure
    const params = new URLSearchParams(locationSearch);
    const subParam = params.get(TicketsRefinementKeysE.SubCategory);
    const subIds = subParam ? subParam.split(',').map((s): string => s.trim()) : [];
    const isAdjusted =
      adjustmentSubCategoryIds.length > 0 &&
      adjustmentSubCategoryIds.length === subIds.length &&
      adjustmentSubCategoryIds.every((id): boolean => subIds.includes(id));

    if (isAdjusted) {
      setActiveScheduleCards([]);
      setActiveScheduleFilters([]);
      return;
    }

    const newActiveCards: string[] = [];
    const newActiveFilters: ScheduleFilterValuesE[] = [];
    const scheduleRangeString = params.getAll(TicketsRefinementKeysE.Schedule)[0]?.split(',') || [];

    if (scheduleRangeString.includes(ScheduleFilterValuesE.LessThan31Days)) {
      newActiveCards.push('dealer-1');
      newActiveFilters.push(ScheduleFilterValuesE.LessThan31Days);
    }
    if (scheduleRangeString?.includes(ScheduleFilterValuesE.From31To60Days)) {
      newActiveCards.push('dealer-2');
      newActiveFilters.push(ScheduleFilterValuesE.From31To60Days);
    }
    if (scheduleRangeString?.includes(ScheduleFilterValuesE.From61To90Days)) {
      newActiveCards.push('dealer-3');
      newActiveFilters.push(ScheduleFilterValuesE.From61To90Days);
    }
    if (scheduleRangeString?.includes(ScheduleFilterValuesE.From91To120Days)) {
      newActiveCards.push('dealer-4');
      newActiveFilters.push(ScheduleFilterValuesE.From91To120Days);
    }
    if (scheduleRangeString?.includes(ScheduleFilterValuesE.Over120Days)) {
      newActiveCards.push('dealer-5');
      newActiveFilters.push(ScheduleFilterValuesE.Over120Days);
    }

    const allDealerCards = ['dealer-1', 'dealer-2', 'dealer-3', 'dealer-4', 'dealer-5'];
    const hasAllDealers = allDealerCards.every((card): boolean => newActiveCards.includes(card));

    if (hasAllDealers) {
      newActiveCards.push('schedule-all');
    }

    setActiveScheduleCards(newActiveCards);
    setActiveScheduleFilters(newActiveFilters);
    // locationSearch + adjustmentSubCategoryIds drive URL → state sync; isAdjusted derived inside effect
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locationSearch, adjustmentSubCategoryIds, setActiveScheduleCards, setActiveScheduleFilters]);

  const toggleFilter = (cardKey: string, filterValue?: ScheduleFilterValuesE): void => {
    let newActiveCards = isAdjustedView ? [] : [...activeScheduleCards];
    let newActiveFilters = isAdjustedView ? [] : [...activeScheduleFilters];

    if (cardKey === 'schedule-all') {
      if (newActiveCards.includes('schedule-all')) {
        // Deselect all cards
        newActiveCards = [];
        newActiveFilters = [];
        updateSearchParamsArray(TicketsRefinementKeysE.Schedule, null);
        updateSearchParamsArray(TicketsRefinementKeysE.SubCategory, null);
        updateSearchParamsArray(TicketsRefinementKeysE.RequestSubCategoryIds, null);
        updateSearchParamsArray(TicketsRefinementKeysE.DMSScheduleAccounts, null);
      } else {
        // Select all cards
        newActiveCards = [
          'schedule-all',
          'dealer-1',
          'dealer-2',
          'dealer-3',
          'dealer-4',
          'dealer-5',
        ];
        newActiveFilters = [
          ScheduleFilterValuesE.LessThan31Days,
          ScheduleFilterValuesE.From31To60Days,
          ScheduleFilterValuesE.From61To90Days,
          ScheduleFilterValuesE.From91To120Days,
          ScheduleFilterValuesE.Over120Days,
        ];
        updateSearchParamsArray(TicketsRefinementKeysE.SubCategory, null);
        updateSearchParamsArray(TicketsRefinementKeysE.RequestSubCategoryIds, null);
      }
    } else {
      const currentIndex = newActiveCards.indexOf(cardKey);

      if (currentIndex === -1) {
        newActiveCards.push(cardKey);
        if (filterValue) {
          newActiveFilters.push(filterValue);
        }
      } else {
        newActiveCards.splice(currentIndex, 1);
        if (filterValue) {
          const filterIndex = newActiveFilters.indexOf(filterValue);
          if (filterIndex !== -1) {
            newActiveFilters.splice(filterIndex, 1);
          }
        }
      }

      // Ensure the "All" card is inactive if any individual card is deselected
      const allIndex = newActiveCards.indexOf('schedule-all');
      if (allIndex !== -1) {
        newActiveCards.splice(allIndex, 1);
      }

      // Activate the "All" card if all individual cards are selected
      if (
        newActiveCards.includes('dealer-1') &&
        newActiveCards.includes('dealer-2') &&
        newActiveCards.includes('dealer-3') &&
        newActiveCards.includes('dealer-4') &&
        newActiveCards.includes('dealer-5')
      ) {
        newActiveCards.push('schedule-all');
      }
    }

    setActiveScheduleCards(newActiveCards);
    setActiveScheduleFilters(newActiveFilters);
    updateSearchParamsArray(TicketsRefinementKeysE.SubCategory, null);
    updateSearchParamsArray(TicketsRefinementKeysE.RequestSubCategoryIds, null);
    updateSearchParamsArray(
      TicketsRefinementKeysE.Schedule,
      newActiveFilters.length > 0 && newActiveFilters.length < 5 ? newActiveFilters : null
    );
  };

  return (
    <ScheduleTilesOuterWrapStyled
      justify="start"
      align="stretch"
      direction="column"
      className="cards-report-view"
    >
      {isLoadingScheduleReport ? (
        <ScheduleCardsLoader />
      ) : (
        <>
          <RowLabelStyled variant="body4">Dealer Schedule</RowLabelStyled>
          <CardsRowStyled justify="start" align="stretch">
            <Tooltip
              key="tooltip-all"
              title={
                scheduleReport?.totalDataBySubCategories &&
                scheduleReport.totalDataBySubCategories.length > 0 ? (
                  <div>
                    {scheduleReport.totalDataBySubCategories?.map((sub, subIndex) => (
                      <FlexContainer key={`sub-${subIndex}`} justify="between" style={{ gap: 12 }}>
                        <p>{sub.subCategoryName}</p>
                        <p>
                          {sub.count} ({formatAmount(sub.amount)})
                        </p>
                      </FlexContainer>
                    ))}
                  </div>
                ) : (
                  ''
                )
              }
              arrow
              placement="bottom"
            >
              <div>
                <CardWrapStyled
                  onClick={(): void => toggleFilter('schedule-all')}
                  isActive={displayDealerCards.includes('schedule-all')}
                  direction="column"
                  justify="start"
                  key="schedule-all"
                >
                  <Box
                    display="flex"
                    flexDirection="column"
                    justifyContent="space-between"
                    width="100%"
                  >
                    <Box
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                      gap={2}
                      mb={2}
                    >
                      <Typography size="sm" color="darkGrey">
                        Accounting Schedule as of
                      </Typography>
                      <AllScheduleIcon />
                    </Box>
                    <Box
                      display="flex"
                      justifyContent="space-between"
                      gap={2}
                      alignItems="baseline"
                    >
                      <Box
                        display="flex"
                        flexDirection="column"
                        py={0}
                        justifyContent="space-between"
                      >
                        <Box>
                          {scheduleReport?.lastUpdatedDateTime && (
                            <Typography
                              title="Last date Schedule uploaded"
                              variant="h6"
                              weight="normal"
                            >
                              {format(
                                new Date(scheduleReport.lastUpdatedDateTime),
                                'MMMM dd, yyyy'
                              )}
                            </Typography>
                          )}
                        </Box>
                        <MUITypography fontWeight="bolder" fontSize={16}>
                          ${scheduleReport?.totalAmount}
                        </MUITypography>
                      </Box>
                      <Box
                        display="flex"
                        flexDirection="column"
                        py={0}
                        justifyContent="space-between"
                      >
                        <Typography variant="body5" color="darkGrey">
                          Tickets
                        </Typography>
                        <Typography variant="h4">{scheduleReport?.totalCount}</Typography>
                      </Box>
                    </Box>
                  </Box>
                </CardWrapStyled>
              </div>
            </Tooltip>
            {scheduleReport?.data.map((report, index: number) => (
              <Tooltip
                key={`tooltip-${index + 1}`}
                title={
                  report.dataBySubCategories && report.dataBySubCategories.length > 0 ? (
                    <div>
                      {report.dataBySubCategories?.map((sub, subIndex) => (
                        <FlexContainer
                          key={`sub-${subIndex}`}
                          justify="between"
                          style={{ gap: 12 }}
                        >
                          <p>{sub.subCategoryName}</p>
                          <p>
                            {sub.count} ({formatAmount(sub.amount)})
                          </p>
                        </FlexContainer>
                      ))}
                    </div>
                  ) : (
                    ''
                  )
                }
                arrow
                placement="bottom"
              >
                <div>
                  <CardWrapStyled
                    key={`dealer-${index + 1}`}
                    onClick={(): void => toggleFilter(`dealer-${index + 1}`, enumMapping[index])}
                    isActive={displayDealerCards.includes(`dealer-${index + 1}`)}
                    direction="column"
                    justify="start"
                  >
                    <ClaimReportInnerContentStyled align="start" direction="column">
                      <InnerContentRowStyled align="center" justify="between">
                        <Typography variant="h5" color="darkGrey" weight="normal">
                          {report.ageRange.from}
                          {report.ageRange.to ? `-${report.ageRange.to}` : '+'} Days
                        </Typography>
                        <ArrowDownIcon />
                      </InnerContentRowStyled>
                      <InnerContentRowStyled align="end" justify="between">
                        <AmountStyled variant="h5" title={report.amount?.toLocaleString()}>
                          ${report.amount?.toLocaleString()}
                        </AmountStyled>
                        <div>
                          <Typography variant="body5" color="darkGrey">
                            Tickets
                          </Typography>
                          <Typography variant="h4">{report.count}</Typography>
                        </div>
                      </InnerContentRowStyled>
                    </ClaimReportInnerContentStyled>
                  </CardWrapStyled>
                </div>
              </Tooltip>
            ))}
          </CardsRowStyled>

          <SuggestedAdjustmentsRowStyled isOpened={isSuggestedAdjustmentsOpened}>
            <RowLabelStyled variant="body4">Adjusted Schedule</RowLabelStyled>
            <CardsRowStyled justify="start" align="stretch">
              <Tooltip
                key="tooltip-adjusted-all"
                title={
                  scheduleReport?.totalAdjustedDataBySubCategories &&
                  scheduleReport.totalAdjustedDataBySubCategories.length > 0 ? (
                    <div>
                      {scheduleReport.totalAdjustedDataBySubCategories?.map((sub, subIndex) => (
                        <FlexContainer
                          key={`adj-sub-${subIndex}`}
                          justify="between"
                          style={{ gap: 12 }}
                        >
                          <p>{sub.subCategoryName}</p>
                          <p>
                            {sub.count} ({formatAmount(sub.amount)})
                          </p>
                        </FlexContainer>
                      ))}
                    </div>
                  ) : (
                    ''
                  )
                }
                arrow
                placement="bottom"
              >
                <div>
                  <CardWrapStyled
                    onClick={(): void => toggleAdjustedFilter('adjusted-all')}
                    isActive={activeAdjustedCards.includes('adjusted-all')}
                    direction="column"
                    justify="start"
                    key="adjusted-all"
                  >
                    <Box
                      display="flex"
                      flexDirection="column"
                      justifyContent="space-between"
                      width="100%"
                    >
                      <Box
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                        gap={2}
                        mb={2}
                      >
                        <Typography size="sm" color="darkGrey">
                          Accounting Schedule as of
                        </Typography>
                        <AllScheduleIcon />
                      </Box>
                      <Box
                        display="flex"
                        justifyContent="space-between"
                        gap={2}
                        alignItems="baseline"
                      >
                        <Box
                          display="flex"
                          flexDirection="column"
                          py={0}
                          justifyContent="space-between"
                        >
                          <Box>
                            {scheduleReport?.lastUpdatedDateTime && (
                              <Typography
                                title="Last date Schedule uploaded"
                                variant="h6"
                                weight="normal"
                              >
                                {format(
                                  new Date(scheduleReport.lastUpdatedDateTime),
                                  'MMMM dd, yyyy'
                                )}
                              </Typography>
                            )}
                          </Box>
                          <MUITypography fontWeight="bolder" fontSize={16}>
                            ${(scheduleReport?.totalAdjustedAmount ?? 0).toLocaleString()}
                          </MUITypography>
                        </Box>
                        <Box
                          display="flex"
                          flexDirection="column"
                          py={0}
                          justifyContent="space-between"
                        >
                          <Typography variant="body5" color="darkGrey">
                            Tickets
                          </Typography>
                          <Typography variant="h4">
                            {scheduleReport?.totalAdjustedCount ?? 0}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  </CardWrapStyled>
                </div>
              </Tooltip>
              {scheduleReport?.data.map((report, index: number) => (
                <Tooltip
                  key={`tooltip-adjusted-${index + 1}`}
                  title={
                    report.adjustedDataBySubCategories &&
                    report.adjustedDataBySubCategories.length > 0 ? (
                      <div>
                        {report.adjustedDataBySubCategories?.map((sub, subIndex) => (
                          <FlexContainer
                            key={`adj-${index}-${subIndex}`}
                            justify="between"
                            style={{ gap: 12 }}
                          >
                            <p>{sub.subCategoryName}</p>
                            <p>
                              {sub.count} ({formatAmount(sub.amount)})
                            </p>
                          </FlexContainer>
                        ))}
                      </div>
                    ) : (
                      ''
                    )
                  }
                  arrow
                  placement="bottom"
                >
                  <div>
                    <CardWrapStyled
                      key={`adjusted-${index + 1}`}
                      onClick={(): void =>
                        toggleAdjustedFilter(`adjusted-${index + 1}`, enumMapping[index])
                      }
                      isActive={activeAdjustedCards.includes(`adjusted-${index + 1}`)}
                      direction="column"
                      justify="start"
                    >
                      <ClaimReportInnerContentStyled align="start" direction="column">
                        <InnerContentRowStyled align="center" justify="between">
                          <Typography variant="h5" color="darkGrey" weight="normal">
                            {report.ageRange.from}
                            {report.ageRange.to ? `-${report.ageRange.to}` : '+'} Days
                          </Typography>
                          <ArrowDownIcon />
                        </InnerContentRowStyled>
                        <InnerContentRowStyled align="end" justify="between">
                          <AmountStyled
                            variant="h5"
                            title={(report.adjustedAmount ?? 0).toLocaleString()}
                          >
                            ${(report.adjustedAmount ?? 0).toLocaleString()}
                          </AmountStyled>
                          <div>
                            <Typography variant="body5" color="darkGrey">
                              Tickets
                            </Typography>
                            <Typography variant="h4">{report.adjustedCount ?? 0}</Typography>
                          </div>
                        </InnerContentRowStyled>
                      </ClaimReportInnerContentStyled>
                    </CardWrapStyled>
                  </div>
                </Tooltip>
              ))}
            </CardsRowStyled>
          </SuggestedAdjustmentsRowStyled>
        </>
      )}
    </ScheduleTilesOuterWrapStyled>
  );
};

export default ScheduleCards;
